import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { vendorUUIDs } from "../../constants";
import { redeemGiftCardLanguages, purchaseGiftCardLanguages } from "./helpers";

import BannerImage from "public/images/stlouisblues/blues-gift-card-banner.jpg";
const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/NHL_2024/STL-Blues-2025.jpg";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

const config: TeamConfig = {
  teamSchema: "stlouisblues_giftcards",
  browserTitle: "St. Louis Blues Gift Cards",
  packages: false,
  defaultRoute: routes.gift,
  phoneNumber: "",
  teamName: "St. Louis Blues",

  // seat map configs
  seatMapConfigs: {
    venueId: "",
    mapId: "",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: true,
    redirectGiftCardUrl: routes.giftCard,
    redeemSuccessModalLink: "https://am.ticketmaster.com/blues",
    hideRedeemItem: true,
    customErrorMessage:
      "There are no gift cards available for purchase at this time, please check in later.",
  },

  // disabled routes
  disabledRoutes: [routes.purchaseSeats],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    checkoutTimerEnabled: false,
    showFrontendTotalAmount: false,
    showFrontendServiceFee: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
    checkoutTimerEnabled: false,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },
  isSaveCardAllowed: true,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: " https://am.ticketmaster.com/blues/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    findGamesBannerImage: "",
    authBackgroundImage: BannerImage.src,
    checkoutStatusPageImage: BannerImage.src,
    giftCardStandalonePageImage: BannerImage.src,
    logoImage: LogoImage + "?width=100&height=76",
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: "",
  },

  colors: {
    primary: "#0072CE",
  },

  howItWorks: [],
  faqs: [],

  uiConfigs: {
    bannerImage: {
      title: "Choose Your Games",
      isBackgroundBasedBanner: true,
    },
    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },
    bottomBar: {
      showGameSelectionProgress: false,
    },
    hamburgerMenu: {
      purchaseHistoryEnabled: false,
    },
  },

  languages: {
    topNavMenuItems: "", // can be react node as well
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: null,
    purchaseGiftCard: purchaseGiftCardLanguages,
    redeemGiftCard: redeemGiftCardLanguages,
    giftCard: {
      title: "Give The Gift Of St. Louis Blues Hockey",
      createGiftCard: {
        description:
          "Share your passion for St. Louis Blues Hockey with a friend or loved one! Upon purchase of a gift card, a unique code will be generated for the intended recipient to redeem. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all St. Louis Blues games in the 2026-2027 season.",
        recipient: {
          sendNowFalse: {
            description:
              "If an intended recipient is not specified, once purchased, you will have to forward the unique code to the recipient. Upon your recipient receiving their gift card, they will be prompted to enter their unique code to redeem the gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all St. Louis Blues games in the 2026-2027 season.",
          },
          sendNowTrue: {
            description:
              "The recipient will be sent an email with a unique code for them to redeem their gift card! Once redeemed, the recipient will receive an account credit that they can use to purchase any St. Louis Blues games in the 2026-2027 season.",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description:
            "Please input the case sensitive unique codes provided to redeem the value of the gift card.",
        },
        logout: {
          description:
            "Please input the case sensitive unique codes provided to redeem the value of the gift card",
        },
        success: {
          description: "has been added to your St. Louis Blues account.",
        },
      },
    },
    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
